<template>
  <div :class="containerClass">
    <template v-if="loading">
      <div class="p-tree-loading-overlay p-component-overlay">
        <i :class="loadingIconClass" />
      </div>
    </template>
    <div class="p-tree-filter-container" v-if="filter">
      <div style="display: flex; flex-direction: row; align-items: center;">
        <div style="flex-grow: 1">
          <input type="text" autocomplete="off" class="p-tree-filter p-inputtext p-component" :placeholder="filterPlaceholder"
                 @keydown="onFilterKeydown" v-model="filterValue" :disabled="disabled"/>
          <span v-if="!filterValue || filterValue.trim().length === 0" class="p-tree-filter-icon pi pi-search" style="right: 5rem"></span>
          <span v-if="filterValue && filterValue.trim().length > 0" class="p-tree-filter-icon pi pi-times" style="cursor:pointer; right: 5rem" @click.prevent="filterValue = ''"></span>
        </div>
        <div @click.prevent="onAdd" style="fill: #1976d2; margin-left: 0.5rem; cursor: pointer; transition: all .2s; max-height: 30px; display: flex;" :class="{'hover': !disabled, 'add-disabled': disabled}"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="24" height="24" viewBox="0 0 24 24"><path d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"></path><!----></svg></div>
        <div @click.prevent="$emit('configure')" style="margin-left: 0.5rem; cursor: pointer; transition: all .2s; max-height: 30px; display: flex;" class="hover"><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="M433-80q-27 0-46.5-18T363-142l-9-66q-13-5-24.5-12T307-235l-62 26q-25 11-50 2t-39-32l-47-82q-14-23-8-49t27-43l53-40q-1-7-1-13.5v-27q0-6.5 1-13.5l-53-40q-21-17-27-43t8-49l47-82q14-23 39-32t50 2l62 26q11-8 23-15t24-12l9-66q4-26 23.5-44t46.5-18h94q27 0 46.5 18t23.5 44l9 66q13 5 24.5 12t22.5 15l62-26q25-11 50-2t39 32l47 82q14 23 8 49t-27 43l-53 40q1 7 1 13.5v27q0 6.5-2 13.5l53 40q21 17 27 43t-8 49l-48 82q-14 23-39 32t-50-2l-60-26q-11 8-23 15t-24 12l-9 66q-4 26-23.5 44T527-80h-94Zm7-80h79l14-106q31-8 57.5-23.5T639-327l99 41 39-68-86-65q5-14 7-29.5t2-31.5q0-16-2-31.5t-7-29.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q22 23 48.5 38.5T427-266l13 106Zm42-180q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Zm-2-140Z"/></svg></div>
      </div>
    </div>
    <ul class="p-tree-container" role="tree">
      <TreeNode v-for="node of valueToRender" :key="node.key" :node="node" :templates="$scopedSlots"
                :expandedKeys="d_expandedKeys" @node-toggle="onNodeToggle" @node-click="onNodeClick" @node-right-click="onNodeRightClick"
                :selectionMode="selectionMode" :selectionKeys="selectionKeys" @checkbox-change="onCheckboxChange" :disabled="disabled"></TreeNode>
    </ul>
  </div>
</template>

<script>
import TreeNode from './TreeNode.vue';
import debounce from 'lodash/debounce';
import ObjectUtils from 'primevue/utils/ObjectUtils';

export default {
  props: {
    value: {
      type: null,
      default: null
    },
    expandedKeys: {
      type: null,
      default: null
    },
    selectionKeys: {
      type: null,
      default: null
    },
    selectionMode: {
      type: String,
      default: null
    },
    metaKeySelection: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingIcon: {
      type: String,
      default: 'pi pi-spinner'
    },
    filter: {
      type: Boolean,
      default: false
    },
    filterBy: {
      type: String,
      default: 'label'
    },
    filterMode: {
      type: String,
      default: 'lenient'
    },
    filterPlaceholder: {
      type: String,
      default: null
    },
    filterLocale: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      d_expandedKeys: this.expandedKeys || {},
      //filterValue: null,
      timeout: null,
      debouncedInput: ''
    }
  },
  watch: {
    expandedKeys(newValue) {
      console.log("new val" + newValue)
      this.d_expandedKeys = newValue;
    }
  },
  methods: {
    onAdd() {
      if (this.disabled) {
        return;
      }
      this.$emit('open-add');
    },
    filteredValue() {
      let filteredNodes = [];
      const searchFields = this.filterBy.split(',');
      const filterText = this.filterValue.trim().toLocaleLowerCase(this.filterLocale);
      const strict = this.filterMode === 'strict';
      this.expands = {};
      let stamm = -1;
      let posNr = -1;
      let splits = filterText.split(".");
      if (splits.length === 2 && !isNaN(splits[0]) && !isNaN(splits[1])) {
        stamm = parseInt(splits[0]);
        posNr = parseInt(splits[1]);
      }
      for (let node of this.value) {
        let _node = {...node};
        let paramsWithoutNode = {searchFields, filterText, strict, stamm, posNr};

        if ((strict && (this.findFilteredNodes(_node, paramsWithoutNode) || this.isFilterMatched(_node, paramsWithoutNode))) ||
            (!strict && (this.isFilterMatched(_node, paramsWithoutNode) || this.findFilteredNodes(_node, paramsWithoutNode)))) {
          filteredNodes.push(_node);
          //expands[_node.key] = true;
        }
      }
      this.d_expandedKeys = {...this.expands}
      return filteredNodes;
    },
    onNodeToggle(node) {
      const key = node.key;

      if (this.d_expandedKeys[key]) {
        delete this.d_expandedKeys[key];
        this.$emit('node-collapse', node);
      }
      else {
        this.d_expandedKeys[key] = true;
        this.$emit('node-expand', node);
      }

      this.d_expandedKeys = {...this.d_expandedKeys};
      this.$emit('update:expandedKeys', this.d_expandedKeys);
    },
    onNodeClick(event) {
      if (this.selectionMode != null && event.node.selectable !== false) {
        const metaSelection = event.nodeTouched ? false : this.metaKeySelection;
        const _selectionKeys = metaSelection ? this.handleSelectionWithMetaKey(event) : this.handleSelectionWithoutMetaKey(event);

        this.$emit('update:selectionKeys', _selectionKeys);
      }
    },
    onNodeRightClick(event) {
      this.$emit("node-right-click", event)
    },
    onCheckboxChange(event) {
      this.$emit('update:selectionKeys', event.selectionKeys);

      if (event.check)
        this.$emit('node-select', event.node);
      else
        this.$emit('node-unselect', event.node);
    },
    handleSelectionWithMetaKey(event) {
      const originalEvent = event.originalEvent;
      const node = event.node;
      const metaKey = (originalEvent.metaKey||originalEvent.ctrlKey);
      const selected = this.isNodeSelected(node);
      let _selectionKeys;

      if (selected && metaKey) {
        if (this.isSingleSelectionMode()) {
          _selectionKeys = {};
        }
        else {
          _selectionKeys = {...this.selectionKeys};
          delete _selectionKeys[node.key];
        }

        this.$emit('node-unselect', node);
      }
      else {
        if (this.isSingleSelectionMode()) {
          _selectionKeys = {};
        }
        else if (this.isMultipleSelectionMode()) {
          _selectionKeys = !metaKey ? {} : (this.selectionKeys ? {...this.selectionKeys} : {});
        }

        _selectionKeys[node.key] = true;
        this.$emit('node-select', node);
      }

      return _selectionKeys;
    },
    handleSelectionWithoutMetaKey(event) {
      const node = event.node;
      const selected = this.isNodeSelected(node);
      let _selectionKeys;

      if (this.isSingleSelectionMode()) {
        if (selected) {
          _selectionKeys = {};
          this.$emit('node-unselect', node);
        }
        else {
          _selectionKeys = {};
          _selectionKeys[node.key] = true;
          this.$emit('node-select', node);
        }
      }
      else {
        if (selected) {
          _selectionKeys = {...this.selectionKeys};
          delete _selectionKeys[node.key];

          this.$emit('node-unselect', node);
        }
        else {
          _selectionKeys = this.selectionKeys ? {...this.selectionKeys} : {};
          _selectionKeys[node.key] = true;

          this.$emit('node-select', node);
        }
      }

      return _selectionKeys;
    },
    isSingleSelectionMode() {
      return this.selectionMode === 'single';
    },
    isMultipleSelectionMode() {
      return this.selectionMode === 'multiple';
    },
    isNodeSelected(node) {
      return (this.selectionMode && this.selectionKeys) ? this.selectionKeys[node.key] === true : false;
    },
    isChecked(node) {
      return this.selectionKeys ? this.selectionKeys[node.key] && this.selectionKeys[node.key].checked: false;
    },
    isNodeLeaf(node) {
      return node.leaf === false ? false : !(node.children && node.children.length);
    },
    onFilterKeydown(event) {
      if (event.which === 13) {
        event.preventDefault();
      }
    },
    findFilteredNodes(node, paramsWithoutNode) {
      if (node) {
        let matched = false;
        if (node.children) {
          let childNodes = [...node.children];
          node.children = [];
          for (let childNode of childNodes) {
            let copyChildNode = {...childNode};
            if (this.isFilterMatched(copyChildNode, paramsWithoutNode)) {
              matched = true;
              this.expands[node.key] = true;
              this.expands[copyChildNode.key] = true;
              node.children.push(copyChildNode);
            }
          }
        }

        if (matched) {
          return true;
        }
      }
    },
    isFilterMatched(node, {searchFields, filterText, strict, stamm, posNr}) {
      let matched = false;
      for(let field of searchFields) {
        let fieldValue = String(ObjectUtils.resolveFieldData(node, field)).toLocaleLowerCase(this.filterLocale);
        //let fieldValue = node[field].toLocaleLowerCase(this.filterLocale);
        if(fieldValue.indexOf(filterText) > -1 || (parseInt(node.data.aufisn) === stamm && parseInt(node.data.pos_nr) === posNr) ) {
          matched = true;
          this.expands[node.key] = true;
        }
      }

      if (!matched || (strict && !this.isNodeLeaf(node))) {
        matched = this.findFilteredNodes(node, {searchFields, filterText, strict, stamm, posNr}) || matched;
      }

      return matched;
    }
  },
  computed: {
    filterValue: {
      get() {
        return this.debouncedInput
      },
      set(val) {
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          this.debouncedInput = val
        }, 300)
      }
    },
    containerClass() {
      return ['p-tree p-component', {
        'p-tree-selectable': this.selectionMode != null,
        'p-tree-loading': this.loading
      }];
    },
    loadingIconClass() {
      return ['p-tree-loading-icon pi-spin', this.loadingIcon];
    },
    valueToRender() {
      if (this.filterValue && this.filterValue.trim().length > 0)
        return this.filteredValue();
      else {
        this.d_expandedKeys = {}
        return this.value;
      }
    }
  },
  components: {
    'TreeNode': TreeNode
  }
}
</script>

<style scoped>
.hover:hover {
  transform: scale(1.1);
  opacity: .7;
}

.add-disabled {
  color: #9e9da2;
  cursor: initial;
  transition: none !important;
  fill: #9e9da2 !important;;
}


</style>

<style>
.p-tree-container {
  margin: 0;
  padding: 0;
  list-style-type: none;
  overflow: visible;
}

.p-treenode-children {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.p-treenode-selectable {
  cursor: pointer;
  user-select: none;
}

.p-tree-toggler {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
}

.p-treenode-leaf > .p-treenode-content .p-tree-toggler {
  visibility: hidden;
}

.p-treenode-content {
  display: flex;
  align-items: center;
}

.p-tree-filter {
  width: 100%;
}

.p-tree-filter-container {
  position: relative;
  display: block;
  width: 100%;
}

.p-tree-filter-icon {
  position: absolute;
  top: 50%;
  margin-top: -.5rem;
}

.p-tree-loading {
  position: relative;
  min-height: 4rem;
}

.p-tree .p-tree-loading-overlay {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
